/**
 * Base axios client + cross-cutting concerns (retry, errors, logging).
 *
 * Extracted from `src/services/api.ts` (Fase 10 Slice 10.1). All API
 * modules under `src/api/` reuse `apiClient` instead of creating their
 * own — keeps a single place for baseURL, timeouts, credentials and
 * Sentry instrumentation.
 *
 * Re-exported from `src/services/api.ts` so existing imports keep
 * working (zero blast radius this slice). Migration of consumers to
 * the new path happens in later slices.
 */

import axios from 'axios';
import * as Sentry from '@sentry/react';

// En desarrollo, CRA proxy redirige /api/v1/* a localhost:8000.
// En producción, /api/v1 apunta directamente al backend.
export const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 segundos
  withCredentials: true, // Send httpOnly cookies with every request
});

// ─── Dev-only logging ──────────────────────────────────────────────────────
if (process.env.NODE_ENV === 'development') {
  apiClient.interceptors.request.use((config) => {
    if (config.url !== '/app-config') {
      // eslint-disable-next-line no-console
      console.log('🚀 API Request:', config.method?.toUpperCase(), config.url);
    }
    return config;
  });

  apiClient.interceptors.response.use(
    (response) => {
      if (response.config.url !== '/app-config') {
        // eslint-disable-next-line no-console
        console.log('✅ API Response:', response.status, response.config.url);
      }
      return response;
    },
    (error) => {
      // Aborted requests (AbortController cleanup — e.g. React StrictMode's
      // double-invoke in dev, or a component unmount) have no response; they are
      // not failures, so don't log them as errors.
      const canceled = axios.isCancel(error) || error?.code === 'ERR_CANCELED';
      if (!canceled && error.config?.url !== '/app-config') {
        // eslint-disable-next-line no-console
        console.error('❌ API Error:', error.response?.status, error.config?.url);
      }
      return Promise.reject(error);
    },
  );
}

// ─── Retry helper for network resilience ────────────────────────────────────

interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
}

function isRetryableError(error: any): boolean {
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') return true;
  if (error.response?.status >= 500) return true;
  return false;
}

/**
 * Retry an async fn up to `maxRetries` times with exponential backoff
 * for retryable errors (network failures, 5xx). Cancellation errors
 * propagate immediately. Non-retryable errors after exhausting attempts
 * (or any 4xx) are thrown — only 5xx/network ones go to Sentry.
 */
export async function withRetry<T>(
  fn: () => Promise<T>,
  { maxRetries = 2, baseDelayMs = 1000 }: RetryOptions = {},
): Promise<T> {
  let lastError: any;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error: any) {
      lastError = error;
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
        throw error; // propagate cancellation, don't retry, don't Sentry
      }
      if (attempt < maxRetries && isRetryableError(error)) {
        const delay = baseDelayMs * Math.pow(2, attempt);
        await new Promise((r) => setTimeout(r, delay));
        continue;
      }
      if (!error.response || error.response.status >= 500) {
        Sentry.captureException(error, {
          extra: { attempt, maxRetries },
        });
      }
      throw error;
    }
  }
  throw lastError;
}

// ─── OutOfStock error class ─────────────────────────────────────────────────

/**
 * Server returns 409 with a structured `OUT_OF_STOCK` detail payload
 * when the user picked recipes that ran out of stock between recipe
 * pick and checkout. The frontend catches this to bounce the user
 * back to step 11 (recipe picker) instead of silently failing.
 */
export interface OutOfStockDetailPayload {
  code?: string;
  detail?: string;
  unavailable_product_ids?: unknown;
  /** Legacy key (pre-rename); read as fallback during rolling deploys. */
  unavailable_variant_ids?: unknown;
}

export class OutOfStockError extends Error {
  readonly code: string;
  readonly unavailableProductIds: string[];
  readonly status: number;

  constructor(message: string, unavailableProductIds: string[], status = 409) {
    super(message);
    this.name = 'OutOfStockError';
    this.code = 'OUT_OF_STOCK';
    this.unavailableProductIds = unavailableProductIds;
    this.status = status;
  }
}

// A Shopify numeric variant id OR a canonical product.id (UUID, for a PrestaShop-only
// recipe with no Shopify variant): the backend reports out-of-stock recipes by
// whichever id the effective channel exposes, so accept both.
const OUT_OF_STOCK_ID_RE = /^\d+$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Converts an axios `error.response.data.detail` payload into a typed error. */
export const toOutOfStockError = (payload: OutOfStockDetailPayload, status: number): OutOfStockError => {
  // Prefer the neutral key; fall back to the legacy `unavailable_variant_ids` during
  // rolling deploys. Content is Shopify variant ids (numeric) or product ids (UUID).
  const raw = payload?.unavailable_product_ids ?? payload?.unavailable_variant_ids;
  const ids = Array.isArray(raw)
    ? raw.map((id) => String(id || '').trim()).filter((id) => OUT_OF_STOCK_ID_RE.test(id))
    : [];
  return new OutOfStockError(payload?.detail || 'Algunas recetas ya no están disponibles por stock.', ids, status);
};

export const isOutOfStockError = (error: unknown): error is OutOfStockError =>
  error instanceof OutOfStockError || (error instanceof Error && (error as OutOfStockError).code === 'OUT_OF_STOCK');
